import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import FormSection from 'application/components/FormSection/FormSection';
import { GamifyIcon } from 'application/components/GamifyIcon';
import {
  StyledTextField,
  FieldLabel,
  StyledButton,
  SecondaryButton,
} from './styledFormComponents';
import { ReviewConfig } from '../MessagesConfig.view';

interface ReviewRequestFormProps {
  config: ReviewConfig;
  onSubmit: (config: ReviewConfig) => void;
  onCancel: () => void;
}

export default function ReviewRequestForm({
  config,
  onSubmit,
  onCancel,
}: ReviewRequestFormProps) {
  const initialValues: ReviewConfig = {
    defaultMessage: config.defaultMessage,
    fromEmail: config.fromEmail,
    reviewDisplayText: config.reviewDisplayText,
    reviewPageUrl: config.reviewPageUrl,
  };
  const validationSchema = Yup.object().shape({
    defaultMessage: Yup.string().required('Default message is required'),
    fromEmail: Yup.string()
      .email('Invalid email address')
      .required('From email is required'),
    reviewDisplayText: Yup.string().required('Review display text is required'),
    reviewPageUrl: Yup.array().of(
      Yup.object().shape({
        ctaText: Yup.string().required('CTA text is required'),
        url: Yup.string().url('Invalid URL').required('URL is required'),
      })
    ),
  });
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({ backgroundColor: theme.custom?.background1 })}
    >
      <Box>
        <Typography
          sx={(theme) => ({
            fontSize: 18,
            fontWeight: 700,
            color: theme.custom?.text,
          })}
        >
          Review Request
        </Typography>
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 400,
            color: theme.custom?.textLight,
          })}
        >
          Your team can view and edit these messages in the Paiv App.
        </Typography>
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
          resetForm,
        }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center', // centers horizontally
              justifyContent: 'flex-start',
              width: '100%',
              mt: 4,
            }}
          >
            <Form
              style={{
                width: '100%', // let it expand fully
                maxWidth: 1000, // 👈 control actual width here
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <FormSection
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  //   gap: 2,
                  width: '100%',
                }}
              >
                <FieldLabel>Default Email/SMS Message *</FieldLabel>
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.custom?.textLight,
                    ml: 1,
                    mb: 1,
                  })}
                >
                  This message will appear in the SMS or Email sent to the
                  customer with the review request page link.
                </Typography>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="defaultMessage"
                  multiline
                  rows={4}
                  placeholder="Enter your default message here"
                  error={touched.defaultMessage && !!errors.defaultMessage}
                  helperText={touched.defaultMessage && errors.defaultMessage}
                />
                <FieldLabel sx={{ mt: 3 }}>
                  Default request page message *
                </FieldLabel>
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.custom?.textLight,
                    ml: 1,
                    mb: 1,
                  })}
                >
                  This message will appear in the request page that the customer
                  will see.
                </Typography>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="reviewDisplayText"
                  multiline
                  rows={4}
                  placeholder="Enter your default request page message here"
                  error={
                    touched.reviewDisplayText && !!errors.reviewDisplayText
                  }
                  helperText={
                    touched.reviewDisplayText && errors.reviewDisplayText
                  }
                />
                <FieldLabel sx={{ mt: 3 }}>From Email *</FieldLabel>
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.custom?.textLight,
                    ml: 1,
                    mb: 1,
                  })}
                >
                  This email will be used as the sender of the review request
                  email.
                </Typography>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="fromEmail"
                  placeholder="Enter your from email here"
                  error={touched.fromEmail && !!errors.fromEmail}
                  helperText={touched.fromEmail && errors.fromEmail}
                />
                <FieldLabel sx={{ mt: 3 }}>Review page links *</FieldLabel>
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.custom?.textLight,
                    ml: 1,
                    mb: 1,
                  })}
                >
                  These links will be used to redirect the customer to the
                  review page.
                </Typography>
                {values.reviewPageUrl.map((link, index, list) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 1,
                      width: '100%',
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Link {index + 1}</FieldLabel>
                      <Field name={`reviewPageUrl.${index}.url`}>
                        {({ field, meta }: any) => (
                          <StyledTextField
                            {...field}
                            fullWidth
                            placeholder="Enter your review page URL here"
                            error={Boolean(meta.touched && meta.error)}
                            helperText={meta.touched && meta.error}
                          />
                        )}
                      </Field>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Label</FieldLabel>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Field name={`reviewPageUrl.${index}.ctaText`}>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              placeholder="Enter your label here"
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>

                        {list.length > 1 && (
                          <Box
                            sx={(theme) => ({
                              width: 48,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: theme.custom?.background3,
                                '& svg': { color: 'red' },
                              },
                            })}
                            onClick={() => {
                              const temp = [...values.reviewPageUrl];
                              temp.splice(index, 1);
                              setFieldValue('reviewPageUrl', temp);
                            }}
                          >
                            <GamifyIcon icon="trash" color="inherit" />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
                <StyledButton
                  sx={{ mt: 1, maxWidth: '200px' }}
                  onClick={() => {
                    setFieldValue('reviewPageUrl', [
                      ...values.reviewPageUrl,
                      { url: '', ctaText: '' },
                    ]);
                  }}
                  startIcon={
                    <GamifyIcon
                      icon="add"
                      color="inherit"
                      backgroundColor="transparent"
                    />
                  }
                >
                  Add Link
                </StyledButton>
              </FormSection>
              <Box sx={{ display: 'flex', mt: 1, gap: 2 }}>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    console.log('cancel');
                    console.log(config);
                    if (config.new) {
                      onCancel();
                    } else {
                      resetForm();
                    }
                  }}
                >
                  {' '}
                  Cancel
                </SecondaryButton>
                <StyledButton
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Save Changes'
                  )}
                </StyledButton>
              </Box>
            </Form>
          </Box>
        )}
      </Formik>
    </Paper>
  );
}
